// The unified Lists page (2026-10-13): every list a person can see in one place, and the
// open list editable in place. Replaces the List Builder (/admin/report-builder, now a redirect) and gives
// partners the lists staff shared with them. Spec: docs/superpowers/specs/2026-10-13-unified-lists-design.md
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { byName } from '../lib/catalogModel';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { AdminSidebar } from '../components/AdminShell';
import ShareReportModal from './ShareReportModal';
import useListEditor from '../components/lists/useListEditor';
import ListIndex from '../components/lists/ListIndex';
import ListHeader from '../components/lists/ListHeader';
import ListFindTab from '../components/lists/ListFindTab';
import ListMembers from '../components/lists/ListMembers';
import UndoToast from '../components/lists/UndoToast';
import './AdminDashboard.css';
import '../components/AdminShell.css';
import '../components/lists/Lists.css';

// Used only if the live sport catalog fails to load.
const SPORTS_FALLBACK = [{ slug: 'football', name: 'Football' }];
const EMPTY_FIND = { text: '', page: 1 };

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
);

export default function ListsPage() {
  const { API_URL } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('list') ? Number(searchParams.get('list')) : null;

  const [index, setIndex] = useState({ mine: [], shared: [], team: [] });
  const [indexLoaded, setIndexLoaded] = useState(false);
  const [indexError, setIndexError] = useState('');
  const [filter, setFilter] = useState('');
  const [sports, setSports] = useState(SPORTS_FALLBACK);
  const [campList, setCampList] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  // The open list's tab ('members' | 'find') and the Find tab's query. The query lives here so it
  // survives tab switches; both reset when a different list opens.
  const [tab, setTab] = useState('members');
  const [findState, setFindState] = useState(EMPTY_FIND);
  const [tabsForId, setTabsForId] = useState(null);

  const cfg = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const loadIndex = useCallback(() => axios.get(`${API_URL}/reports/index`, cfg())
    .then((r) => { setIndex({ mine: r.data.mine || [], shared: r.data.shared || [], team: r.data.team || [] }); setIndexError(''); })
    .catch((e) => setIndexError(e.response?.data?.detail || 'Could not load your lists.'))
    .finally(() => setIndexLoaded(true)), [API_URL, cfg]);

  const editor = useListEditor({ onChanged: loadIndex });
  const { open } = editor;

  useEffect(() => { loadIndex(); }, [loadIndex]);
  useEffect(() => {
    axios.get(`${API_URL}/sports/catalog`)
      .then((res) => {
        const list = (res.data.sports || []).map((s) => ({ slug: s.slug, name: s.name, positions: (s.positions || []).map((x) => x.label).filter(Boolean) })).sort(byName);
        if (list.length) setSports(list);
      }).catch(() => { /* keep the fallback */ });
    axios.get(`${API_URL}/camps?include_past=true`, cfg())
      .then((res) => setCampList((res.data || []).slice().sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))))
      .catch(() => { /* camp: tokens just match nothing */ });
  }, [API_URL, cfg]);

  // Land on a real list: the first of mine, else the first shared; fall back when the open one is gone.
  useEffect(() => {
    if (!indexLoaded) return;
    const all = [...index.mine, ...index.shared, ...index.team];
    if (selectedId != null && all.some((r) => r.id === selectedId)) return;
    setSearchParams(all[0] ? { list: String(all[0].id) } : {}, { replace: true });
  }, [indexLoaded, index, selectedId, setSearchParams]);

  useEffect(() => { if (selectedId != null) open(selectedId); }, [selectedId, open]);

  const select = (id) => { setShareOpen(false); setSearchParams({ list: String(id) }); };

  const createList = async () => {
    // Football by name, not sports[0]: the list is alphabetical, so the first entry is no longer the default.
    const sport = editor.list?.sport || (sports.some((s) => s.slug === 'football') ? 'football' : sports[0]?.slug) || 'football';
    const id = await editor.createNew(sport);
    if (id) { await loadIndex(); setJustCreated(id); setSearchParams({ list: String(id) }); }
  };

  const deleteList = () => {
    if (!editor.list) return;
    if (window.confirm(`Delete list "${editor.list.title}"? This cannot be undone.`)) editor.remove();
  };

  // Open a list someone gave me the code for. The server records the open, which is what lists it under
  // "Shared with me"; then reload the index and land on it. Returns an error message, or '' on success.
  const openByCode = async (code) => {
    try {
      const res = await axios.post(`${API_URL}/reports/${encodeURIComponent(code)}/open`, {}, cfg());
      await loadIndex();
      select(res.data.report_id);
      return '';
    } catch (e) {
      return e.response?.status === 404 ? 'No list has that code.' : (e.response?.data?.detail || 'Could not open that list.');
    }
  };

  // A shared list is view-only; this makes my own editable copy (athletes only, the grade/notes stay with the owner).
  const copyToMine = async () => {
    if (!editor.list) return;
    try {
      const res = await axios.post(`${API_URL}/reports`, {
        title: editor.list.title, sport: editor.list.sport,
        athlete_user_ids: editor.list.members.map((m) => m.user_id),
      }, cfg());
      await loadIndex();
      setSearchParams({ list: String(res.data.id) });
    } catch (e) {
      setIndexError(e.response?.data?.detail || 'Could not copy that list.');
    }
  };

  const list = editor.list;
  // A different list just opened: land on its members, or on Find when it is empty. Adjusted during
  // render (not in an effect) so the first paint of the new list already shows the right tab.
  const listId = list ? list.id : null;
  if (listId !== tabsForId) {
    setTabsForId(listId);
    setTab(list && list.members.length ? 'members' : 'find');
    setFindState(EMPTY_FIND);
  }
  const showFind = editor.canEdit && tab === 'find';
  const ownerName = list ? ([...index.shared, ...index.team].find((r) => r.id === list.id) || {}).owner_name : null;
  const positions = useMemo(() => (list && list.sport !== 'football'
    ? ((sports.find((s) => s.slug === list.sport) || {}).positions || []) : []), [list, sports]);
  const addedUid = editor.lastAction && editor.lastAction.kind === 'add' ? editor.lastAction.uid : null;

  return (
    <div className="adm">
      <div className="adm__shell">
        <AdminSidebar active="lists" />
        <main className="adm__main ls-main">
          {indexError && <div role="alert" className="settings-toast settings-toast--error">{indexError}</div>}
          <div className="ls-grid">
            <ListIndex mine={index.mine} shared={index.shared} team={index.team} selectedId={selectedId}
              onSelect={select} onNew={createList} filter={filter} onFilter={setFilter} onOpenCode={openByCode} />
            <section className="adm__card ls-open" data-testid="lists_open">
              {list ? (
                <>
                  <ListHeader list={list} canEdit={editor.canEdit} sports={sports} ownerName={ownerName}
                    autoFocusTitle={justCreated === list.id} onRename={editor.rename}
                    onChangeSport={editor.changeSport} onShare={() => setShareOpen(true)} onDelete={deleteList}
                    onCopyToMine={copyToMine} />
                  {editor.error && <div role="alert" className="ls-banner ls-banner--error" data-testid="list_error">{editor.error}</div>}
                  {editor.notice && <div className="ls-banner" role="status" data-testid="list_notice">{editor.notice}</div>}
                  {editor.canEdit && (
                    <div className="ls-tabs" data-testid="list_tabs">
                      <div className="ls-seg" role="tablist" aria-label="List view">
                        <button type="button" role="tab" id="list-tab-members" aria-selected={tab === 'members'}
                          className={`ls-seg__btn${tab === 'members' ? ' ls-seg__btn--active' : ''}`}
                          onClick={() => setTab('members')} data-testid="list_tab_members">
                          {`On this list · ${list.members.length}`}
                        </button>
                        <button type="button" role="tab" id="list-tab-find" aria-selected={tab === 'find'}
                          className={`ls-seg__btn${tab === 'find' ? ' ls-seg__btn--active' : ''}`}
                          onClick={() => setTab('find')} data-testid="list_tab_find">
                          <PlusIcon />Find &amp; add athletes
                        </button>
                      </div>
                      {showFind && (
                        <span className="ls-find__scope" data-testid="list_find_scope">
                          {list.sport === 'football' ? 'Football only' : 'This sport only'}
                        </span>
                      )}
                    </div>
                  )}
                  <div role={editor.canEdit ? 'tabpanel' : undefined}
                    aria-labelledby={editor.canEdit ? (showFind ? 'list-tab-find' : 'list-tab-members') : undefined}>
                    {showFind ? (
                      <ListFindTab sport={list.sport} campList={campList} members={list.members} positions={positions}
                        onAdd={editor.addMember} state={findState} onState={setFindState} />
                    ) : (
                      <ListMembers members={list.members} canEdit={editor.canEdit} addedUid={addedUid} onRemove={editor.removeMember}
                        onUpdate={editor.updateMember} />
                    )}
                  </div>
                  <UndoToast action={editor.lastAction} listTitle={list.title} onUndo={editor.undo} onClose={editor.dismissLastAction} />
                  {shareOpen && (
                    <ShareReportModal reportId={list.id} reportCode={list.code} reportTitle={list.title}
                      reportSport={list.sport} reportCount={list.members.length} onClose={() => setShareOpen(false)} />
                  )}
                </>
              ) : (
                <p className="ls-empty ls-empty--pane">
                  {indexLoaded && !index.mine.length && !index.shared.length && !index.team.length
                    ? 'No lists yet. Create your first list to start collecting athletes.'
                    : (editor.error || 'Select a list to open it.')}
                </p>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
